import { useSearch } from "@tanstack/react-router";
import type { AssignableRole } from "@unbox/api";
import { Plus, SearchLg, UserPlus01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useMe } from "@/api/me";
import {
  type TeamRole,
  useAddTeamMember,
  useRemoveTeamMember,
  useResendTeamInvite,
  useRevokeTeamInvite,
  useTeam,
  useUpdateTeamInviteRole,
  useUpdateTeamMemberRole,
} from "@/api/team";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { PageLayout } from "@/components/page-layout";
import { TeamConfirmModal } from "@/components/team/team-confirm-modal";
import { type TeamMemberFormValues, TeamMemberModal } from "@/components/team/team-member-modal";
import { type TeamInviteRow, type TeamRow, TeamTable } from "@/components/team/team-table";
import { usePermissions } from "@/hooks/use-permissions";
import { useMerchant } from "@/providers/MerchantProvider";
import { error, success } from "@/providers/ToastProvider";

const toMessage = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/** Rows the page can edit only ever hold a brand-scoped role; a Workspace Admin's
 *  row is blocked from editing because there is no per-brand grant to change. */
/** The page manages one brand, so a row's grants name it and nothing else. */
const brandRole = ({ brands }: { brands: { role: TeamRole }[] }): TeamRole => brands[0]?.role ?? "user";

const toBrandRole = (role: AssignableRole): TeamRole => (role === "merchant_admin" ? "merchant_admin" : "user");

export function TeamPage() {
  const merchantId = useMerchant();
  const { data: me } = useMe();
  const { invitableRoles } = usePermissions();
  const { data, isLoading } = useTeam();
  const { sort } = useSearch({ strict: false }) as { sort?: "role" };

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<TeamRow | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [removing, setRemoving] = useState<TeamRow | null>(null);

  const addMember = useAddTeamMember();
  const updateRole = useUpdateTeamMemberRole();
  const updateInviteRole = useUpdateTeamInviteRole();
  const removeMember = useRemoveTeamMember();
  const resendInvite = useResendTeamInvite();
  const revokeInvite = useRevokeTeamInvite();

  const merchant = useMemo(() => {
    const match = me?.merchants?.find((m) => m.id === merchantId);
    return { name: match?.name ?? "this brand", logoUrl: match?.logoUrl ?? null };
  }, [me?.merchants, merchantId]);

  // Only the brand-scoped roles are relevant here; a Workspace Admin viewer can
  // grant workspace_admin elsewhere, but not on a single brand.
  const assignableRoles = useMemo(
    () => invitableRoles.filter((role): role is AssignableRole => role !== "workspace_admin"),
    [invitableRoles],
  );

  const rows: TeamRow[] = useMemo(() => {
    if (!data) return [];

    const invites: TeamRow[] = data.invites.map((invite) => ({
      kind: "invite",
      id: `invite-${invite.id}`,
      inviteId: invite.id,
      email: invite.email,
      roles: [brandRole(invite)],
      createdAt: new Date(invite.createdAt),
      expired: new Date(invite.expiresAt) <= new Date(),
    }));

    const members: TeamRow[] = data.members.map((member) => ({
      kind: "member" as const,
      id: `member-${member.id}`,
      userId: member.id,
      email: member.email,
      name: `${member.firstName} ${member.lastName}`.trim(),
      roles: [member.isWorkspaceAdmin ? ("workspace_admin" as const) : brandRole(member)],
      lastActivity: member.lastActivity ? new Date(member.lastActivity) : null,
      createdAt: new Date(member.createdAt),
      managedAtWorkspaceLevel: member.isWorkspaceAdmin,
      isSelf: member.id === me?.id,
    }));

    return [...invites, ...members];
  }, [data, me?.id]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (row) =>
        row.email.toLowerCase().includes(term) || (row.kind === "member" && row.name.toLowerCase().includes(term)),
    );
  }, [rows, search]);

  const submitMember = async ({ email, role }: TeamMemberFormValues) => {
    try {
      if (!editing) {
        const result = await addMember.mutateAsync({ email, role });
        success(result.type === "invite" ? "Invite sent" : "Added to the team");
        setIsAdding(false);
        return;
      }

      if (editing.kind === "member") {
        await updateRole.mutateAsync({ userId: editing.userId, role });
      } else {
        await updateInviteRole.mutateAsync({ inviteId: editing.inviteId, role });
      }
      success("Role updated");
      setEditing(null);
    } catch (e) {
      error(toMessage(e, "Something went wrong"));
    }
  };

  const onResend = async (row: TeamInviteRow) => {
    try {
      await resendInvite.mutateAsync(row.inviteId);
      success("Invite sent");
    } catch (e) {
      error(toMessage(e, "Could not resend the invite"));
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    try {
      if (removing.kind === "invite") {
        await revokeInvite.mutateAsync(removing.inviteId);
        success("Invite revoked");
      } else {
        await removeMember.mutateAsync(removing.userId);
        success("Access removed");
      }
      setRemoving(null);
    } catch (e) {
      error(toMessage(e, "Could not complete that action"));
    }
  };

  return (
    <PageLayout
      title="Team"
      icon={UserPlus01}
      subtitle="Manage all team members with access"
      callToAction={
        <div className="flex items-center gap-3">
          <Input
            aria-label="Search Team"
            placeholder="Search Team"
            icon={SearchLg}
            value={search}
            onChange={setSearch}
            wrapperClassName="w-[280px]"
          />
          <Button color="primary" size="md" iconLeading={Plus} onClick={() => setIsAdding(true)}>
            team member
          </Button>
        </div>
      }
    >
      <div className="rounded-lg border border-primary bg-primary overflow-hidden">
        {isLoading ? (
          <p className="text-sm text-tertiary p-6">Loading team...</p>
        ) : (
          <TeamTable
            rows={visibleRows}
            assignableRoles={assignableRoles}
            onEdit={setEditing}
            onRemove={setRemoving}
            onResend={onResend}
            emptyMessage={
              search.trim() ? "No team members match your search." : "Team members with access will appear here."
            }
            initialSort={sort === "role" ? { column: "role", direction: "asc" } : undefined}
          />
        )}
      </div>

      <TeamMemberModal
        isOpen={isAdding || !!editing}
        onOpenChange={(open) => {
          if (open) return;
          setIsAdding(false);
          setEditing(null);
        }}
        merchant={merchant}
        assignableRoles={assignableRoles}
        editing={editing ? { email: editing.email, role: toBrandRole(editing.roles[0]) } : null}
        onSubmit={submitMember}
        isPending={addMember.isPending || updateRole.isPending || updateInviteRole.isPending}
      />

      <TeamConfirmModal
        isOpen={!!removing}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={removing?.kind === "invite" ? "Revoke this invite?" : "Remove access?"}
        description={
          removing?.kind === "invite"
            ? `${removing.email} will no longer be able to use their invite link.`
            : `${removing?.email} will immediately lose access to ${merchant.name}.`
        }
        confirmLabel={removing?.kind === "invite" ? "Revoke invite" : "Remove access"}
        onConfirm={confirmRemove}
        isPending={removeMember.isPending || revokeInvite.isPending}
      />
    </PageLayout>
  );
}
